// Registro consensi GDPR firmati + editor del testo dell'informativa (admin)
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSignature, Download, Save, Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDT = (d) => (d ? `${d.slice(0, 10).split("-").reverse().join("/")} ${d.slice(11, 16)}` : "—");

export default function GdprAdminDialog({ open, onClose }) {
  const [tab, setTab] = useState("consents");
  const [consents, setConsents] = useState(null);
  const [text, setText] = useState("");
  const [version, setVersion] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab("consents");
    axios.get(`${API}/admin/gdpr/consents`).then((r) => setConsents(r.data)).catch(() => setConsents([]));
    axios.get(`${API}/admin/gdpr/text`).then((r) => { setText(r.data.text); setVersion(r.data.version); }).catch(() => {});
  }, [open]);

  const downloadPdf = async (c) => {
    try {
      const r = await axios.get(`${API}/admin/gdpr/consents/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `consenso_gdpr_${(c.user_email || "utente").split("@")[0]}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download PDF"); }
  };

  const saveText = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/gdpr/text`, { text });
      setVersion(r.data.version);
      toast.success(`Informativa aggiornata (versione ${r.data.version})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="gdpr-admin-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#8A1C2A]" />Consensi GDPR</DialogTitle>
        </DialogHeader>
        <div className="flex gap-1 border-b border-stone-200 shrink-0">
          <button onClick={() => setTab("consents")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "consents" ? "border-[#8A1C2A] text-[#8A1C2A]" : "border-transparent text-stone-500"}`} data-testid="gdpr-tab-consents">Firme registrate</button>
          <button onClick={() => setTab("text")} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === "text" ? "border-[#8A1C2A] text-[#8A1C2A]" : "border-transparent text-stone-500"}`} data-testid="gdpr-tab-text">Testo informativa</button>
        </div>

        {tab === "consents" && (
          <div className="overflow-y-auto flex-1 -mx-1 px-1">
            {consents === null && <p className="text-sm text-stone-400 p-4">Caricamento…</p>}
            {consents?.length === 0 && <p className="text-sm text-stone-400 p-6 text-center" data-testid="gdpr-no-consents">Nessuna firma ancora registrata.</p>}
            <div className="space-y-2 py-2">
              {consents?.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border border-stone-200 rounded-lg p-3" data-testid={`gdpr-consent-${c.id}`}>
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-semibold text-sm text-stone-900 flex items-center gap-2">
                      <FileSignature className="w-4 h-4 text-emerald-600" /> {c.signed_name}
                      <Badge className="bg-stone-100 text-stone-600 border border-stone-200 text-[10px]">v{c.text_version}</Badge>
                    </div>
                    <p className="text-xs text-stone-500">{c.user_email} · ruolo {c.role}</p>
                  </div>
                  <div className="text-right text-xs text-stone-500">
                    <p className="font-mono">{fmtDT(c.signed_at)}</p>
                    <p>IP {c.ip || "—"}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadPdf(c)} data-testid={`gdpr-pdf-${c.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" /> PDF
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "text" && (
          <div className="flex flex-col gap-3 flex-1 overflow-hidden">
            <p className="text-xs text-stone-500 shrink-0">Versione attuale: <b>v{version}</b> — salvando si crea una nuova versione; le firme già raccolte conservano il testo firmato all'epoca.</p>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} className="flex-1 min-h-[320px] font-mono text-xs leading-relaxed" data-testid="gdpr-text-editor" />
            <div className="shrink-0">
              <Button onClick={saveText} disabled={saving} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="gdpr-text-save">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Salva nuova versione
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
